<script lang="ts">
export default {
  name: 'StoreConnectAccount',
}
</script>
<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useForm, useField } from 'vee-validate'
import { storeToRefs } from 'pinia'
import { useLocationConnectStore } from '../../domains/location/stores'
import { _sleep } from '../../modules/sleep'
import {
  KkButton,
  KkBox,
  KkFormLabel,
  KkTextField,
  KkValidationError,
  KkLoading,
  KkTooltip,
} from '../../components/base'
import {
  KkInfo,
} from '../../components/ui'
import type {
  CreateConnectApiParams,
  UpdateConnectApiParams,
} from '../../domains/location/stores'
import { castSnakeToCamel } from '../../modules/string_converter'
import { siteInfoMap } from 'src/biz_spa/modules/sitesInfoMapConst'

/** Interface */
interface ReadLocationStoreParams {
  company_uuid: string
  store_uuid: string
}

/** Variable */
const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const emit = defineEmits(['update-title'])
const convertingStoreId = ref<boolean>(false)
const isHoverDescriptionWithShopUrl = ref<boolean>(false)
const isHoverDescriptionWithStoreId = ref<boolean>(false)

/** component / element refs */
const submitRef = ref<HTMLButtonElement>()

/** Computed */
const siteName = computed<string>(() => route.params.site_name as string)
const companyUuid = computed<string>(() => route.params.company_uuid as string)
const storeUuid = computed<string>(() => route.params.store_uuid as string)
const isUpdate = computed<boolean>(() => {
  return Boolean(connect.value.review.url || connect.value.account.identifier)
})
const submittable = computed<boolean>(() => {
  const { dirty, valid } = meta.value
  if (dirty && valid && !isLoading.value && !isWriting.value && !locationStoreLoading.value && !convertingStoreId.value) {
    // 編集時
    if (isUpdate.value) {
      return Boolean(
        reviewUrl.value || accountId.value || accountPassword.value || storeId.value
      )
    }
    // 作成時かつ店舗IDが必要なメディア
    else if (connect.value.meta.connectableAccountStoreIdentifier) {
      return Boolean(
        reviewUrl.value ||
        (accountId.value && accountPassword.value && storeId.value)
      )
    }
    // 作成時かつ店舗IDが不必要なメディア
    else {
      return Boolean(
        reviewUrl.value ||
        (accountId.value && accountPassword.value)
      )
    }
  } else {
    return false
  }
})

/** Form */
const { handleSubmit, meta } = useForm({
  validationSchema: {
    reviewUrl: `${ siteName.value }_url`,
  },
  initialValues: {
    reviewUrl: '',
    accountId: '',
    accountPassword: '',
    storeId: '',
  },
})

const {
  value: reviewUrl,
  errorMessage: reviewUrlError,
  handleChange: updateReviewUrl
} = useField<string>('reviewUrl', undefined, { label: t('pages.LocationStoreConnectAccount.form.labels.reviewUrl') })

const {
  value: accountId,
  errorMessage: accountIdError,
  handleChange: updateAccountId
} = useField<string>('accountId', undefined, { label: t('pages.LocationStoreConnectAccount.form.labels.accountId') })

const {
  value: accountPassword,
  errorMessage: accountPasswordError,
  handleChange: updateAccountPassword
} = useField<string>('accountPassword', undefined, { label: t('pages.LocationStoreConnectAccount.form.labels.accountPassword') })

const {
  value: storeId,
  errorMessage: storeIdError,
  handleChange: updateStoreId
} = useField<string>('storeId', undefined, { label: t('pages.LocationStoreConnectAccount.form.labels.storeId') })

/** Pinia Store */
const locationConnectStore = useLocationConnectStore()
const { connect, isLoading, isWriting } = storeToRefs(locationConnectStore)
const { readConnect, createConnect, updateConnect } = locationConnectStore

/** Vuex Store: state */
const locationStore = computed(() => store.state.companiesLocationStore.data)
const locationStoreLoading = computed<boolean>(() => store.state.companiesLocationStore.loading)

/** Vuex Store: getters */
const providedFeaturesLinkUrl = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesLinkUrl'])
const providedFeaturesExternalAccount = computed<boolean>(() => store.getters['companiesCompany/providedFeaturesExternalAccount'])
const taishuTenpyoOption = computed<boolean>(() => store.getters['companiesCompany/taishuTenpyoOption'])

/** Vuex Store: action */
const readLocationStore = (payload: ReadLocationStoreParams) => store.dispatch('companiesLocationStore/readStore', payload)

/** Lifecycle */
readLocationStore({ company_uuid: companyUuid.value, store_uuid: storeUuid.value })
readConnect({ companyUuid: companyUuid.value, storeUuid: storeUuid.value, siteType: siteName.value })

/** Watch */
watch(locationStore, () => {
  if (!locationStore.value) return
  emit('update-title', t('pages.LocationStoreConnectAccount.title', {
    name: locationStore.value.attributes.name,
    siteName: t(`labels.sites.${ castSnakeToCamel(siteName.value) }`),
  }))
})
watch(connect, () => {
  if (connect.value.review.url) {
    reviewUrl.value = connect.value.review.url
  }
  if (connect.value.account.identifier) {
    accountId.value = connect.value.account.identifier
  }
  if (connect.value.account.storeIdentifier) {
    storeId.value = connect.value.account.storeIdentifier
  }
  if (connect.value.review.url && !connect.value.account.storeIdentifier) {
    convertStoreIdField(connect.value.review.url)
  }
})

/** Methods */
const redirect = () => {
  router.push({
    name: 'LocationStoreConnects',
    params: { company_uuid: companyUuid.value, store_uuid: storeUuid.value },
  })
}
const blurReviewUrlField = (event: FocusEvent) => {
  console.log(submitRef.value)
  if (event.relatedTarget && submitRef.value && submitRef.value.isEqualNode(event.relatedTarget as Node)) return

  if (reviewUrl.value) {
    convertStoreIdField(reviewUrl.value)
  }
}
const blurStoreIdField = (event: FocusEvent) => {
  console.log(submitRef.value)
  if (event.relatedTarget && submitRef.value && submitRef.value.isEqualNode(event.relatedTarget as Node)) return

  if (storeId.value) {
    convertStoreIdField(storeId.value)
  }
}
const formatUrl = (url: string) => {
  const result = url.split(/[?#]/)[0]
  return result.replace(/\/$/, '')
}
const isUrl = (text: string) => {
  const result = text.match(/(http|https):\/\/.+/)
  return Boolean(result)
}
const convertStoreIdField = async (url: string): Promise<void> => {
  if (siteName.value === 'taishu_tenpyo' || !isUrl(url)) return
  const words = formatUrl(url).split('/')

  if (words.length > 1) {
    convertingStoreId.value = true
    await _sleep(1800)
    storeId.value = words[words.length - 1]
    convertingStoreId.value = false
    return
  } else {
    return
  }
}
const confirmConnectAccount = (siteType: string) => {
  if(['tabelog', 'retty', 'gnavi', 'hot_pepper_gourmet'].includes(siteType)) {
    return confirm(t('pages.LocationStoreConnectAccount.form.confirmMessage'))
  }
  return true
}
const onSubmit = handleSubmit(async values => {
  if (connect.value.meta.connectableAccountStoreIdentifier && (connect.value.review.url || values.reviewUrl)) {
    await convertStoreIdField(connect.value.review.url || values.reviewUrl)
  }

  const params: CreateConnectApiParams | UpdateConnectApiParams = {
    companyUuid: companyUuid.value,
    storeUuid: storeUuid.value,
    siteType: siteName.value,
    params: {
      review: {
        url: values.reviewUrl
      },
      account: {
        identifier: values.accountId,
        password: values.accountPassword,
        storeIdentifier: storeId.value,
      },
    }
  }
  if (!values.reviewUrl) delete params.params.review
  if(values.accountId && values.accountPassword && !confirmConnectAccount(siteName.value)) return

  if (isUpdate.value) {
    if (!values.accountId && !values.accountPassword) {
      delete params.params.account
    }
    if (params.params.account) {
      if (!values.accountId) delete params.params.account.identifier
      if (!values.accountPassword) delete params.params.account.password
    }
    const result = await updateConnect(params as UpdateConnectApiParams)
    if (result === 'success') redirect()

  } else {
    if (!values.accountId || !values.accountPassword) delete params.params.account
    const result = await createConnect(params as CreateConnectApiParams)
    if (result === 'success') redirect()
  }
})
</script>

<template>
  <p v-if="siteName === 'taishu_tenpyo' &&!taishuTenpyoOption" class="unauthorized">
    {{ t('pages.LocationStoreConnectAccount.unauthorized') }}
  </p>
  <form v-else class="storeConnectAccountForm" @submit.prevent="onSubmit">
    <KkLoading v-if="isLoading" min-height="120px" />
    <KkBox v-else tag="div">
      <template #content>
        <div class="storeConnectAccountForm__container">
          <div v-if="providedFeaturesLinkUrl && connect.meta.connectableReview" class="storeConnectAccountForm__section">
            <KkBox tag="div" border :title="t('pages.LocationStoreConnectAccount.getReviews')">
              <template #content>
                <div class="storeConnectAccountForm__formItem__info">
                  <KkInfo icon="hint">
                    {{ t('pages.LocationStoreConnectAccount.info.storeUrl') }}
                  </KkInfo>
                </div>
                <KkTooltip
                  :is-visible="isHoverDescriptionWithShopUrl"
                  top="25px"
                  left="0px"
                  width="500px"
                  :label="$t('pages.LocationStoreConnectAccount.info.shopUrl')"
                  font-weight="bold"
                  font-size="12px"
                  class="formLabel"
                  @showTooltip="isHoverDescriptionWithShopUrl = true"
                  @hideTooltip="isHoverDescriptionWithShopUrl = false"
                >
                  <p>{{ t('pages.LocationStoreConnectAccount.info.enterShopUrl') }}</p>
                  <p>{{ t('pages.LocationStoreConnectAccount.info.exampleUrl') }}</p>
                  <ul>
                    <li v-for="url in siteInfoMap[siteName]['sampleURLs']" :key="url" class="sampleUrls">
                      {{ url }}
                    </li>
                  </ul>
                </KkTooltip>
                <KkTextField
                  :model-value="reviewUrl"
                  :placeholder="t('messages.noSettings')"
                  @update:model-value="updateReviewUrl"
                  @blur="blurReviewUrlField"
                />
                <KkValidationError :text="reviewUrlError" />
              </template>
            </KkBox>
          </div>
          <div v-if="providedFeaturesExternalAccount && connect.meta.connectableAccount" class="storeConnectAccountForm__section">
            <div class="storeConnectAccountForm__info">
              <KkInfo v-if="siteName === 'yahooLoco'" icon="hint">
                {{ t('pages.LocationStoreConnectAccount.info.yahooLoco') }}
              </KkInfo>
            </div>
            <KkBox tag="div" border :title="t('pages.LocationStoreConnectAccount.linkingStore')">
              <template #content>
                <div class="storeConnectAccountForm__formItem">
                  <div class="storeConnectAccountForm__formItem__info">
                    <KkInfo icon="hint">
                      {{ t('pages.LocationStoreConnectAccount.info.storeId') }}
                    </KkInfo>
                  </div>
                  <KkFormLabel :text="t('pages.LocationStoreConnectAccount.form.labels.accountId')" />
                  <KkTextField
                    :model-value="accountId"
                    :placeholder="t('messages.noSettings')"
                    autocomplete="new-password"
                    @update:model-value="updateAccountId"
                  />
                  <KkValidationError :text="accountIdError" />
                </div>
                <div class="storeConnectAccountForm__formItem">
                  <KkFormLabel :text="t('pages.LocationStoreConnectAccount.form.labels.accountPassword')" />
                  <KkTextField
                    :model-value="accountPassword"
                    type="password"
                    :placeholder="t('messages.noSettings')"
                    autocomplete="new-password"
                    @update:model-value="updateAccountPassword"
                  />
                  <KkValidationError :text="accountPasswordError" />
                </div>
                <div v-if="connect.meta.connectableAccountStoreIdentifier" class="storeConnectAccountForm__formItem">
                  <p v-if="siteName === 'taishu_tenpyo'" class="storeNameLabel">
                    {{ $t('pages.LocationStoreConnectAccount.form.labels.taishuTenpyoStoreName') }}
                  </p>
                  <KkTooltip
                    v-else
                    :is-visible="isHoverDescriptionWithStoreId"
                    top="25px"
                    left="0px"
                    width="500px"
                    :label="$t('pages.LocationStoreConnectAccount.form.labels.storeId')"
                    font-weight="bold"
                    font-size="12px"
                    class="formLabel"
                    @showTooltip="isHoverDescriptionWithStoreId = true"
                    @hideTooltip="isHoverDescriptionWithStoreId = false"
                  >
                    <p>{{ t('pages.LocationStoreConnectAccount.info.enterShopUrl') }}</p>
                    <p>{{ t('pages.LocationStoreConnectAccount.info.exampleUrl') }}</p>
                    <ul>
                      <li v-for="url in siteInfoMap[siteName]['sampleURLs']" :key="url" class="sampleUrls">
                        {{ url }}
                      </li>
                    </ul>
                  </KkTooltip>
                  <KkTextField
                    :model-value="storeId"
                    :placeholder="t('messages.noSettings')"
                    autocomplete="off"
                    :loading="convertingStoreId"
                    @update:model-value="updateStoreId"
                    @blur="blurStoreIdField"
                  />
                  <KkValidationError :text="storeIdError" />
                </div>
              </template>
            </KkBox>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="storeConnectAccountForm__buttons">
          <KkButton
            color="secondary"
            :to="{ name: 'LocationStoreConnects', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
            :text="t('labels.cancel')"
          />
          <KkButton
            ref="submitRef"
            type="submit"
            color="primary"
            :text="t('labels.save')"
            :disabled="!submittable"
          />
        </div>
      </template>
    </KkBox>
  </form>
</template>

<style lang="scss" scoped>
@import "../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../stylesheets/biz_spa/foundation/mixin";
.storeConnectAccountForm {
  $root: #{&};
  &__container {
    margin-bottom: 86px;
  }
  &__section {
    &:not(:first-child) {
      margin-top: 32px;
    }
  }
  &__formItem {
    &:not(:first-child) {
      margin-top: 32px;
    }
    &__info {
      margin-top: 16px;
      &:first-child {
        margin-top: 0;
      }
      + .formLabel {
        margin-top: 16px;
      }
    }
  }
  &__info {
    margin-bottom: 16px;
  }
  &__buttons {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
  }
}
.sampleUrls {
  word-wrap: break-word;
}
.storeNameLabel {
  font-weight: bold;
}
.unauthorized {
  margin-top: 32px;
}
</style>
